import { LinearProgress, Paper } from '@material-ui/core'
import cx from 'clsx'
import Downshift, { DownshiftState, StateChangeOptions } from 'downshift'
import { ControllerStateAndHelpers } from 'downshift'
import debounce from 'just-debounce'
import { ChangeEvent, FormEvent, useCallback, useMemo, useState } from 'react'

import { detectMessage } from './detect-message'
import { FAQ, SearchProps } from './interface'
import { renderInput, renderSuggestion } from './renderer'

const MIN_SEARCH_LENGTH = 2

// 検索に必要な最小文字数をチェックする関数
const isValidSearchInput = (value: string | null | undefined): boolean => {
  return Boolean(value && value.length >= MIN_SEARCH_LENGTH)
}

export function Search({ search, onSearched, onClick, searchWaitMs, resultMessage }: SearchProps) {
  const [inputValue, setInputValue] = useState('')
  const [menuIsOpen, setMenuIsOpen] = useState(false)
  const [searchResults, setSearchResults] = useState<FAQ[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<boolean>(false)

  // デバウンス検索
  const performSearch = useCallback(
    async (value: string) => {
      if (!isValidSearchInput(value)) {
        setMenuIsOpen(false)
        setIsLoading(false)
        setError(false)
        setSearchResults([])
        return
      }

      try {
        const result = await search(value)
        const faqs = result?.data?.queryFAQ || []
        setSearchResults(faqs)
        setMenuIsOpen(true)
        onSearched(value, faqs)
      } catch (err) {
        setError(true)
        setSearchResults([])
      } finally {
        setIsLoading(false)
      }
    },
    [search, onSearched]
  )

  // just-debounceを使用したメモ化デバウンス関数
  const debouncedSearch = useMemo(
    () => debounce(performSearch, searchWaitMs, false, true),
    [performSearch, searchWaitMs]
  )

  // 入力値変更時の処理
  const handleInputChange = useCallback(
    (value: string) => {
      if (!menuIsOpen) {
        setMenuIsOpen(true)
        setIsLoading(true)
      }
      setInputValue(value)
      debouncedSearch(value)
    },
    [debouncedSearch, menuIsOpen]
  )

  // フォーム送信処理
  const handleSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault()
      performSearch(inputValue)
    },
    [inputValue, performSearch]
  )

  // 外部クリック処理
  const handleOuterClick = useCallback(() => {
    setMenuIsOpen(false)
  }, [])

  // 候補選択処理
  const handleSelect = useCallback(
    (item: FAQ | null) => {
      if (!item) return

      onSearched(inputValue, searchResults)
      onClick(`/answer/${item.id}?query=${encodeURIComponent(inputValue)}`)

      setMenuIsOpen(false)
      setInputValue('')
    },
    [inputValue, searchResults, onSearched, onClick]
  )

  const reducer = useCallback((state: DownshiftState<FAQ>, changes: StateChangeOptions<FAQ>) => {
    switch (changes.type) {
      case Downshift.stateChangeTypes.changeInput: {
        const open = isValidSearchInput(changes.inputValue)
        setMenuIsOpen(open)
        return {
          ...changes,
          isOpen: open,
        }
      }
      case Downshift.stateChangeTypes.blurInput:
      case Downshift.stateChangeTypes.mouseUp:
        return {
          ...changes,
          inputValue: state.inputValue,
          isOpen: state.isOpen,
        }
      case Downshift.stateChangeTypes.keyDownEnter:
        return {
          ...changes,
          inputValue: state.inputValue,
        }
      default:
        return changes
    }
  }, [])

  const renderContent = (
    getItemProps: ControllerStateAndHelpers<FAQ>['getItemProps'],
    highlightedIndex: number
  ) => {
    // 入力が最小文字数未満の場合は何も表示しない
    if (!isValidSearchInput(inputValue)) {
      return null
    }

    // ローディング中の場合
    if (isLoading) {
      return (
        <Paper className={cx('faq-search-result', 'faq-search-container__paper')} square>
          <LinearProgress className="faq-search-container__paper__progress" />
        </Paper>
      )
    }

    // 検索結果がある場合
    if (searchResults.length > 0) {
      return (
        <Paper className={cx('faq-search-result', 'faq-search-container__paper')} square>
          {searchResults.map((suggestion, index) =>
            renderSuggestion({
              suggestion,
              index,
              highlightedIndex,
              inputValue,
              itemProps: getItemProps({
                item: suggestion,
              }),
              onClick: (item: FAQ) => handleSelect(item),
            })
          )}
        </Paper>
      )
    }

    // 検索結果が0件の場合（エラーメッセージ表示）
    return (
      <Paper className={cx('faq-search-result', 'faq-search-container__paper')} square>
        <p className="faq-search-result__message">{detectMessage(error, resultMessage)}</p>
      </Paper>
    )
  }

  return (
    <Downshift<FAQ>
      id="downshift-simple"
      isOpen={menuIsOpen}
      onOuterClick={handleOuterClick}
      stateReducer={reducer}
      itemToString={item => item?.title || ''}
    >
      {({ getInputProps, getItemProps, getMenuProps, highlightedIndex }) => (
        <form className="faq-search-container" onSubmit={handleSubmit}>
          {renderInput({
            InputProps: getInputProps({
              placeholder: '質問内容をご入力ください',
              value: inputValue,
              onChange: (e: ChangeEvent<HTMLInputElement>) => handleInputChange(e.target.value),
            }),
          })}
          <div {...getMenuProps()}>
            {menuIsOpen ? renderContent(getItemProps, highlightedIndex ?? -1) : null}
          </div>
        </form>
      )}
    </Downshift>
  )
}
